import { useNavigate } from 'react-router-dom';
import { AppShell, Content } from '../components/ui/AppShell';
import { DesktopColumns } from '../components/ui/DesktopColumns';
import { Check, ChevronLeft, Crown, Users, TrendingUp, Zap } from '../components/ui/icons';
import { useHaptics } from '../hooks/useHaptics';
import { useAuth } from '../context/AuthContext';
import { useProfile, useUpdateUserDoc } from '../hooks/useQueries';

const PERKS: { Icon: typeof Users; title: string; body: string }[] = [
  { Icon: Users, title: 'Private leagues', body: 'Spin up unlimited friends-only leagues with custom invites.' },
  { Icon: TrendingUp, title: 'Deep stats', body: 'Predictor DNA, market hit-rates and division insights.' },
  { Icon: Zap, title: 'Ad-free season', body: 'A clean, focused experience all campaign long.' }
];

/** Pro upsell. No billing is wired yet, so the CTA registers interest. */
export default function GoPro() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { uid } = useAuth();
  const { data: profile } = useProfile(uid);
  const updateDoc = useUpdateUserDoc(uid);
  // Persisted on the player's own profile doc (the one client-writable doc), so the
  // waitlist survives refresh and is queryable for the real Pro launch.
  const joined = profile?.proWaitlistAt != null;

  return (
    <AppShell>
      <Content>
        <div className="flex items-center justify-between pt-2 md:pt-5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="tap -ml-1 flex items-center gap-[2px] text-ink"
          >
            <ChevronLeft width={22} height={22} strokeWidth={2.4} />
            <span className="text-[16px] font-semibold tracking-snug">Settings</span>
          </button>
        </div>

        <DesktopColumns
          railFirst
          asidePosition="left"
          asideWidth="380px"
          aside={
            <>
        {/* hero */}
              <div className="relative mt-4 overflow-hidden rounded-[26px] bg-[linear-gradient(135deg,#FCDE7C_0%,#E3A82B_100%)] p-6 shadow-[0_18px_34px_-18px_rgba(180,130,30,0.6)] md:sticky md:top-4">
          <div className="pointer-events-none absolute -right-8 -top-10 h-[160px] w-[160px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.35),transparent_70%)]" />
          <div className="relative">
            <div className="grid h-12 w-12 place-items-center rounded-[14px] border border-[#7A5410]/20 bg-[#7A5410]/16">
              <Crown width={26} height={26} strokeWidth={2} style={{ color: '#7A5410' }} />
            </div>
            <h1 className="mt-4 text-[30px] font-extrabold leading-[1.02] tracking-[-0.03em] text-[#5E3F09]">
              HeadToHead Pro
            </h1>
            <p className="mt-2 max-w-[300px] text-[14px] font-semibold leading-[1.4] text-[#7A5410]">
              Private leagues, deep stats and an ad-free season — for the players who live for the ladder.
            </p>
          </div>
        </div>
            </>
          }
          main={
            <>
        {/* perks */}
               <div className="mt-5 flex flex-col gap-3 md:mt-4">
          {PERKS.map((p) => (
            <div
              key={p.title}
              className="flex items-center gap-[14px] rounded-2xl border border-black/5 bg-card p-4 shadow-tile"
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[13px] bg-gradient-to-br from-gold-top to-gold-bottom">
                <p.Icon width={22} height={22} strokeWidth={2} style={{ color: '#7A5410' }} />
              </span>
              <div className="min-w-0">
                <div className="text-[15px] font-bold tracking-snug text-ink">{p.title}</div>
                <div className="text-[13px] font-medium text-ink2">{p.body}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="pt-6">
          <button
            type="button"
            disabled={joined || updateDoc.isPending}
            onClick={() => {
              impact('medium');
              if (!joined) updateDoc.mutate({ proWaitlistAt: Date.now() });
            }}
            className="tap flex w-full items-center justify-center gap-2 rounded-full bg-night py-[16px] text-[15px] font-extrabold text-white shadow-dark disabled:opacity-100"
          >
            {joined ? (
              <>
                <Check width={17} height={17} strokeWidth={3} className="text-accent" />
                You’re on the waitlist
              </>
            ) : (
              'Join the Pro waitlist'
            )}
          </button>
          <p className="pt-3 text-center text-[12px] font-medium text-ink2">
            {joined
              ? 'We’ll let you know the moment Pro launches.'
              : 'Pro is coming soon — be first in line when it goes live.'}
          </p>
        </div>
            </>
          }
        />
      </Content>
    </AppShell>
  );
}
